import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { API, setToken } from "../utils/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        let detail = "Credenciales inválidas";
        try { const d = await res.json(); if (d?.detail) detail = d.detail; }
        catch (jsonErr) { console.debug("[login] response body not JSON:", jsonErr?.message); }
        setError(detail);
        return;
      }
      const data = await res.json();
      setToken(data.token);
      // Role-based redirect: clients go to their dashboard
      if (data.role === "client" && data.client_token) {
        navigate(`/client/${data.client_token}`, { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch {
      setError("Error de conexión. Verifica tu internet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
      {/* Video de fondo */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
        style={{ opacity: 0.35 }}
      >
        <source
          src="https://customer-assets.emergentagent.com/job_5c81eca5-82cd-4071-a85b-2346e52a1b97/artifacts/jh39v1rk_Video_de_Fa%CC%81brica_de_Pa%CC%81ginas_Web%20%281%29.mp4"
          type="video/mp4"
        />
      </video>
      {/* Overlay oscuro */}
      <div className="absolute inset-0 bg-black/80 z-0 pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm animate-fadeInUp">

        {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <img src="/logo.png" alt="Sitios YA" className="h-32 w-auto object-contain mb-5 drop-shadow-2xl" />
          <p className="text-sm text-gray-400 tracking-wide">Inicia sesión para continuar</p>
        </div>

        {/* Card */}
        <div className="bg-[#161616] border border-white/10 rounded-3xl p-6 shadow-2xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && (
              <div
                data-testid="login-error"
                className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl"
              >
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-gray-400 block mb-1.5 uppercase tracking-wide">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                autoComplete="email"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50 focus:bg-white/8 transition-colors"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Contraseña</label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-gray-500 hover:text-[#ffc700] transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#ffc700]/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#ffc700] text-black rounded-2xl font-black text-sm hover:bg-[#e6b300] transition-colors disabled:opacity-60 mt-1 tracking-wide"
            >
              {loading ? <span className="spinner border-black/30 border-t-black" /> : <LogIn size={16} />}
              {loading ? "Iniciando sesión..." : "Iniciar sesión"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6">
          Sitios YA &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
